import React, { useState, useEffect, useRef } from 'react';
import { usePS2Graphics } from '../context/PS2GraphicsContext';

/**
 * PS2BootAnimation — Recreates the iconic PlayStation 2 startup sequence.
 * 
 * Sequence:
 * 1. Black void with floating translucent tower pillars rising & converging
 * 2. Pillars spiral inward toward center
 * 3. "PlayStation 2" styled logo fades in with lens flare
 * 4. Quick fade to site content
 * 
 * Triggers when ps2Mode is toggled ON. Pure CSS + canvas animation.
 */
export default function PS2BootAnimation() {
  const { ps2Mode } = usePS2Graphics();
  const [showBoot, setShowBoot] = useState(false);
  const [waitingForClick, setWaitingForClick] = useState(false); // initial load needs user gesture for audio
  const [phase, setPhase] = useState(0); // 0=pillars, 1=logo, 2=fadeout
  const [booting, setBooting] = useState(false); // true once animation sequence is running
  const prevPs2Mode = useRef(null);
  const hasBooted = useRef(false);
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const audioRef = useRef(null);

  const playStartupSound = () => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      audioRef.current = new Audio('/ps2-startup.mp3');
      audioRef.current.volume = 0.6;
      audioRef.current.play().catch(() => {});
    } catch {
      // Ignore audio errors
    }
  };

  const beginSequence = () => {
    setWaitingForClick(false);
    setBooting(true);
    setPhase(0);
    playStartupSound();
  };

  // Initial page load ONLY — show boot animation once if PS2 mode is active on load
  useEffect(() => {
    if (ps2Mode && !hasBooted.current) {
      hasBooted.current = true;
      setShowBoot(true);
      setWaitingForClick(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase timer management — only runs once booting is true
  useEffect(() => {
    if (!booting) return;

    // Phase 1: Logo reveal after pillars converge
    const t1 = setTimeout(() => setPhase(1), 6500);
    // Phase 2: Fade out
    const t2 = setTimeout(() => setPhase(2), 9200);
    // Cleanup
    const t3 = setTimeout(() => { setShowBoot(false); setBooting(false); }, 10000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [booting]);

  // Canvas pillar animation
  useEffect(() => {
    if (!showBoot || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let w = canvas.width = window.innerWidth;
    let h = canvas.height = window.innerHeight;

    // Generate tower pillars
    const pillars = Array.from({ length: 48 }, (_, i) => ({
      // Start scattered, converge to center
      angle: (i / 48) * Math.PI * 2 + Math.random() * 0.3,
      radius: 200 + Math.random() * 400,
      height: 40 + Math.random() * 180,
      width: 8 + Math.random() * 18,
      speed: 0.3 + Math.random() * 0.5,
      riseDelay: Math.random() * 3.0,
      opacity: 0.15 + Math.random() * 0.35,
      hue: 200 + Math.random() * 40, // blue-ish like PS2
      yOffset: h + Math.random() * 200,
    }));

    // Floating particles (memory card sparkles)
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.5,
      vy: -Math.random() * 1.5 - 0.3,
      size: Math.random() * 2 + 0.5,
      opacity: Math.random() * 0.5,
      life: Math.random(),
    }));

    let startTime = Date.now();

    const draw = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      ctx.clearRect(0, 0, w, h);

      // Deep dark blue-black background
      ctx.fillStyle = '#000008';
      ctx.fillRect(0, 0, w, h);

      // Subtle radial glow at center
      const centerGlow = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, 300);
      centerGlow.addColorStop(0, `rgba(6, 182, 212, ${Math.min(elapsed * 0.01, 0.08)})`);
      centerGlow.addColorStop(1, 'transparent');
      ctx.fillStyle = centerGlow;
      ctx.fillRect(0, 0, w, h);

      // Draw pillars
      const cx = w / 2;
      const cy = h / 2;

      pillars.forEach(p => {
        const pillarElapsed = Math.max(0, elapsed - p.riseDelay);
        if (pillarElapsed <= 0) return;

        // Rise from bottom
        const riseProgress = Math.min(pillarElapsed / 3.5, 1);
        const easeRise = 1 - Math.pow(1 - riseProgress, 3); // ease out cubic

        // Converge toward center over time
        const convergeProgress = Math.min(Math.max(elapsed - 3.0, 0) / 4.0, 1);
        const easeConverge = convergeProgress * convergeProgress; // ease in quad

        const currentRadius = p.radius * (1 - easeConverge * 0.85);
        const currentAngle = p.angle + elapsed * p.speed * 0.15;

        const px = cx + Math.cos(currentAngle) * currentRadius;
        const py = cy + Math.sin(currentAngle) * currentRadius * 0.4; // flatten for perspective

        // Y position: rise from below
        const baseY = h + 100 - easeRise * (h * 0.6 + p.height);
        const finalY = baseY + py - cy;

        // Pillar 3D-ish rendering
        const pillarOpacity = p.opacity * easeRise * (1 - easeConverge * 0.3);

        // Main pillar body
        ctx.save();
        ctx.translate(px, finalY);

        // Subtle rotation for 3D feel
        const skew = Math.sin(currentAngle) * 0.1;
        ctx.transform(1, skew, 0, 1, 0, 0);

        // Pillar gradient
        const grad = ctx.createLinearGradient(0, 0, 0, -p.height * easeRise);
        grad.addColorStop(0, `hsla(${p.hue}, 60%, 30%, ${pillarOpacity * 0.3})`);
        grad.addColorStop(0.5, `hsla(${p.hue}, 70%, 55%, ${pillarOpacity})`);
        grad.addColorStop(1, `hsla(${p.hue}, 60%, 40%, ${pillarOpacity * 0.5})`);

        ctx.fillStyle = grad;
        ctx.fillRect(-p.width / 2, 0, p.width, -p.height * easeRise);

        // Pillar edge highlight
        ctx.fillStyle = `hsla(${p.hue}, 80%, 75%, ${pillarOpacity * 0.4})`;
        ctx.fillRect(-p.width / 2, 0, 1.5, -p.height * easeRise);

        // Top cap glow
        ctx.beginPath();
        ctx.arc(0, -p.height * easeRise, p.width * 0.6, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue}, 70%, 60%, ${pillarOpacity * 0.3})`;
        ctx.fill();

        ctx.restore();
      });

      // Floating particles
      particles.forEach(pt => {
        pt.x += pt.vx;
        pt.y += pt.vy;
        pt.life -= 0.0015;

        if (pt.y < 0 || pt.life <= 0) {
          pt.x = Math.random() * w;
          pt.y = h + 10;
          pt.life = 1;
        }

        ctx.beginPath();
        ctx.arc(pt.x, pt.y, pt.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(147, 197, 253, ${pt.opacity * pt.life * Math.min(elapsed * 0.5, 1)})`;
        ctx.fill();
      });

      // Center convergence flash (at ~5.5s)
      if (elapsed > 5.0 && elapsed < 7.5) {
        const flashProgress = (elapsed - 5.0) / 2.5;
        const flashOpacity = Math.sin(flashProgress * Math.PI) * 0.4;
        const flashGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 200 + flashProgress * 300);
        flashGrad.addColorStop(0, `rgba(6, 182, 212, ${flashOpacity})`);
        flashGrad.addColorStop(0.5, `rgba(59, 130, 246, ${flashOpacity * 0.3})`);
        flashGrad.addColorStop(1, 'transparent');
        ctx.fillStyle = flashGrad;
        ctx.fillRect(0, 0, w, h);
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [booting]);

  if (!showBoot) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{
        opacity: phase === 2 ? 0 : 1,
        transition: 'opacity 0.6s ease-out',
        pointerEvents: phase === 2 ? 'none' : 'auto',
        background: '#000008',
      }}
    >
      {/* Canvas layer: pillars + particles (only when booting) */}
      {booting && <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />}

      {/* Click to enter prompt — shown on initial page load */}
      {waitingForClick && (
        <button
          onClick={beginSequence}
          className="relative z-20 flex flex-col items-center gap-6 cursor-pointer group"
        >
          {/* Pulsing PS2 vertical bars */}
          <div className="flex items-end gap-[3px] opacity-40 group-hover:opacity-80 transition-opacity duration-500">
            {[18, 28, 38, 28, 18].map((h, i) => (
              <div
                key={i}
                className="rounded-sm"
                style={{
                  width: '5px',
                  height: `${h}px`,
                  background: 'linear-gradient(to top, rgba(6,182,212,0.3), rgba(6,182,212,0.7), rgba(147,197,253,0.9))',
                  boxShadow: '0 0 8px rgba(6,182,212,0.3)',
                  animation: `boot-bar-rise 1.5s ease-out ${i * 0.08}s both`,
                }}
              />
            ))}
          </div>
          <div className="text-cyan-400/60 group-hover:text-cyan-400 text-sm font-mono tracking-[0.3em] uppercase transition-all duration-500"
            style={{ animation: 'pulse 2s ease-in-out infinite' }}
          >
            Click to Enter
          </div>
          <div className="text-white/20 text-[10px] font-mono tracking-wider">
            THE COUNTRY CLUB
          </div>
        </button>
      )}

      {/* Logo reveal layer (only during active boot sequence) */}
      {booting && <div
        className="relative z-10 flex flex-col items-center gap-4 select-none"
        style={{
          opacity: phase >= 1 ? 1 : 0,
          transform: phase >= 1 ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(20px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
        }}
      >
        {/* Horizontal lens flare line */}
        <div
          className="absolute w-[600px] h-[2px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(6,182,212,0.6), rgba(255,255,255,0.9), rgba(6,182,212,0.6), transparent)',
            opacity: phase >= 1 ? 1 : 0,
            transform: `scaleX(${phase >= 1 ? 1 : 0})`,
            transition: 'transform 1s ease-out 0.2s, opacity 0.5s ease-out',
            filter: 'blur(1px)',
            boxShadow: '0 0 30px rgba(6,182,212,0.5), 0 0 60px rgba(6,182,212,0.2)',
          }}
        />

        {/* PS2-style vertical bars */}
        <div className="flex items-end gap-[3px] mb-2">
          {[28, 42, 56, 42, 28].map((h, i) => (
            <div
              key={i}
              className="rounded-sm"
              style={{
                width: '6px',
                height: `${h}px`,
                background: `linear-gradient(to top, rgba(6,182,212,0.3), rgba(6,182,212,0.8), rgba(147,197,253,0.9))`,
                opacity: phase >= 1 ? 1 : 0,
                transform: `scaleY(${phase >= 1 ? 1 : 0})`,
                transformOrigin: 'bottom',
                transition: `transform 0.6s ease-out ${0.1 + i * 0.08}s, opacity 0.4s ease-out ${i * 0.08}s`,
                boxShadow: '0 0 8px rgba(6,182,212,0.4)',
              }}
            />
          ))}
        </div>

        {/* Logo text */}
        <div className="text-center">
          <div
            className="text-3xl md:text-4xl font-black tracking-[0.2em] font-mono"
            style={{
              background: 'linear-gradient(180deg, #93c5fd, #06b6d4, #3b82f6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: 'none',
              filter: 'drop-shadow(0 0 20px rgba(6,182,212,0.5))',
            }}
          >
            THE COUNTRY CLUB
          </div>
          <div
            className="text-[11px] font-mono tracking-[0.5em] text-cyan-400/70 mt-2 uppercase"
            style={{
              opacity: phase >= 1 ? 1 : 0,
              transition: 'opacity 0.8s ease-out 0.5s',
            }}
          >
            PS2 Online Revival
          </div>
        </div>
      </div>}

      {/* Skip hint (only during boot sequence) */}
      {booting && <button
        onClick={() => {
          setPhase(2);
          // Fade out audio
          if (audioRef.current) {
            const audio = audioRef.current;
            const fadeOut = setInterval(() => {
              if (audio.volume > 0.05) { audio.volume = Math.max(0, audio.volume - 0.05); }
              else { clearInterval(fadeOut); audio.pause(); }
            }, 30);
          }
          setTimeout(() => { setShowBoot(false); setBooting(false); }, 600);
        }}
        className="absolute bottom-8 text-[10px] font-mono text-white/20 hover:text-white/50 transition-colors z-20 cursor-pointer"
        style={{
          opacity: phase < 2 ? 1 : 0,
          transition: 'opacity 0.3s',
        }}
      >
        Press to skip
      </button>}

      {/* Build version — bottom right */}
      <div
        className="absolute bottom-8 right-8 text-[10px] font-mono text-white/15 select-none z-10 tracking-wider"
        style={{
          opacity: phase < 2 ? 1 : 0,
          transition: 'opacity 0.3s',
        }}
      >
        Prior: {typeof __COMMIT_HASH__ !== 'undefined' ? __COMMIT_HASH__ : 'dev'}
      </div>
    </div>
  );
}
